import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DEFAULT_METADATA_RECENT_ARTICLES,
    fetchWatchlistNewsMetadata,
} from '../services/newsService';
import {
    loadReadArticleIds,
    markArticlesReadBatch,
} from '../services/newsReadStateService';

/**
 * Watchlist News metadata polling hook (Layer 1).
 *
 * Owns the `{newsByIdentity, moverThresholdPct, loading, error, lastUpdatedAt,
 * readArticleIds, markArticleRead, recencyWindowHours}` slice consumed by the
 * WatchlistTable / NewsCell for showing canonical-identity indicators and the
 * big-mover badge. Article bodies are loaded separately when a popover opens.
 *
 * Behaviour per Decision 11 in NEWS_FEED_LIVING_SPEC.md:
 *   - Poll `/api/news/watchlist_feed` every 5 minutes while the dashboard
 *     is mounted and news is enabled.
 *   - Refetch immediately on tab-focus (visibilitychange -> visible).
 *   - Clear the interval + listener on unmount (no leaks).
 *
 * W8 (2026-08-04) additions:
 *   - Load the user's Firestore read-state on mount into `readArticleIds`
 *     (`Set<article_id>`). Failure to reach Firestore is soft — the set stays
 *     empty and the news feed still renders.
 *   - Expose `markArticleRead(article_id | article_id[])` which writes to
 *     Firestore via a batched writer (short debounce coalesces the N-per-
 *     popover-open burst into a single batch commit) and optimistically
 *     updates the local set.
 *   - Accept an optional `recencyWindowHours` prop (1 / 2 / 6 / 24 / 48 hr per
 *     W8 spec) and re-expose it verbatim so `NewsCell` can drive its dot
 *     without knowing about user prefs. Default 24 hr matches the W7 hardcode.
 *
 * News is a soft signal — errors are logged and surfaced via `error` but must
 * never break watchlist rendering. Callers pass `{}` when `newsByIdentity` is
 * empty; every WatchlistTable row degrades gracefully.
 */

export const WATCHLIST_NEWS_POLL_MS = 5 * 60 * 1000;
export const INITIAL_NEWS_POLL_MS = 30 * 1000;
export const INITIAL_NEWS_WINDOW_MS = 5 * 60 * 1000;
export const DEFAULT_NEWS_RECENCY_WINDOW_HOURS = 24;
// Coalesce the N-per-popover-open burst into a single Firestore batch. Short
// enough to feel synchronous, long enough to catch every article in the
// popover's mark-read call.
export const MARK_READ_DEBOUNCE_MS = 120;

function indexByIdentity(items = []) {
    const map = {};
    (Array.isArray(items) ? items : []).forEach((item) => {
        const cogniveId = item?.cognive_id == null ? '' : String(item.cognive_id).trim();
        if (!cogniveId) return;
        map[cogniveId] = item;
    });
    return map;
}

function parseAsOf(raw) {
    if (!raw) return new Date();
    const parsed = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function normalizeArticleIdInput(raw) {
    if (raw === null || raw === undefined) return '';
    const text = String(raw).trim();
    return text || '';
}

export default function useWatchlistNews({
    userUid,
    enabled = true,
    recentArticlesPerSecurity = DEFAULT_METADATA_RECENT_ARTICLES,
    pollIntervalMs = WATCHLIST_NEWS_POLL_MS,
    recencyWindowHours = DEFAULT_NEWS_RECENCY_WINDOW_HOURS,
    refreshKey = null,
    fetchMetadata = fetchWatchlistNewsMetadata,
    loadReadState = loadReadArticleIds,
    markReadBatch = markArticlesReadBatch,
} = {}) {
    const active = Boolean(userUid && enabled);
    const [newsByIdentity, setNewsByIdentity] = useState({});
    const [moverThresholdPct, setMoverThresholdPct] = useState(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [readArticleIds, setReadArticleIds] = useState(() => new Set());

    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    const lastFetchTsRef = useRef(0);
    const lastAttemptTsRef = useRef(0);
    const hydrationStartedRef = useRef(0);
    const pendingHydrationRef = useRef(true);
    const fetchingRef = useRef(false);

    // Coalesce N mark-read requests fired within MARK_READ_DEBOUNCE_MS into
    // one Firestore batch commit.
    const pendingReadRef = useRef(new Set());
    const flushTimerRef = useRef(null);
    const userUidRef = useRef(userUid);
    useEffect(() => {
        userUidRef.current = userUid;
    }, [userUid]);

    const refresh = useCallback(async () => {
        if (!userUid || !enabled) {
            return null;
        }
        const requestId = ++requestIdRef.current;
        fetchingRef.current = true;
        lastAttemptTsRef.current = Date.now();
        setLoading(true);
        try {
            const resp = await fetchMetadata(recentArticlesPerSecurity);
            if (!mountedRef.current || requestId !== requestIdRef.current) {
                return null;
            }
            pendingHydrationRef.current = (resp?.items || []).some(
                (item) => ['pending', 'retrying'].includes(item?.fetch_status),
            );
            setNewsByIdentity(indexByIdentity(resp?.items));
            if (resp?.mover_threshold_pct != null) {
                const nextThreshold = Number(resp.mover_threshold_pct);
                if (Number.isFinite(nextThreshold)) {
                    setMoverThresholdPct(nextThreshold);
                }
            }
            setLastUpdatedAt(parseAsOf(resp?.as_of));
            lastFetchTsRef.current = Date.now();
            setError(null);
            return resp;
        } catch (err) {
            if (!mountedRef.current || requestId !== requestIdRef.current) {
                return null;
            }
            console.error('Failed to fetch watchlist news metadata:', err);
            setError(err);
            return null;
        } finally {
            if (mountedRef.current && requestId === requestIdRef.current) {
                fetchingRef.current = false;
                setLoading(false);
            }
        }
    }, [userUid, enabled, recentArticlesPerSecurity, fetchMetadata]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!active) {
            requestIdRef.current += 1;
            lastFetchTsRef.current = 0;
            return undefined;
        }

        hydrationStartedRef.current = Date.now();
        pendingHydrationRef.current = true;
        setNewsByIdentity({});
        const effectiveInterval = () => (
            pendingHydrationRef.current && Date.now() - hydrationStartedRef.current < INITIAL_NEWS_WINDOW_MS
                ? Math.min(pollIntervalMs, INITIAL_NEWS_POLL_MS)
                : pollIntervalMs
        );
        // Fire an immediate fetch as soon as the dashboard mounts / news
        // becomes enabled. Subsequent polls run every `pollIntervalMs`.
        void refresh();

        const intervalId = window.setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                // Skip polling while the tab is backgrounded — the
                // visibility listener below will refetch when it returns.
                return;
            }
            if (fetchingRef.current || Date.now() - lastAttemptTsRef.current < effectiveInterval()) return;
            void refresh();
        }, Math.min(pollIntervalMs, INITIAL_NEWS_POLL_MS));

        const handleVisibilityChange = () => {
            if (typeof document === 'undefined') return;
            if (document.visibilityState !== 'visible') return;
            if (fetchingRef.current || (Date.now() - lastFetchTsRef.current) < effectiveInterval()) return;
            void refresh();
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
        }

        return () => {
            requestIdRef.current += 1;
            fetchingRef.current = false;
            window.clearInterval(intervalId);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            }
        };
    }, [active, pollIntervalMs, refresh, refreshKey]);

    // Load the Firestore read-state set once per (userUid, enabled) transition.
    // Failure is swallowed to keep news rendering alive when Firestore is
    // unreachable — see loadReadArticleIds for the soft-fail contract.
    useEffect(() => {
        if (!active) {
            setReadArticleIds(new Set());
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const ids = await loadReadState(userUid);
            if (cancelled || !mountedRef.current) return;
            const nextSet = ids instanceof Set ? ids : new Set(ids || []);
            setReadArticleIds(nextSet);
        })();
        return () => {
            cancelled = true;
        };
    }, [active, userUid, loadReadState]);

    const flushPendingReads = useCallback(async () => {
        flushTimerRef.current = null;
        const uid = userUidRef.current;
        if (!uid || pendingReadRef.current.size === 0) {
            pendingReadRef.current = new Set();
            return;
        }
        const ids = Array.from(pendingReadRef.current);
        pendingReadRef.current = new Set();
        await markReadBatch(uid, ids);
    }, [markReadBatch]);

    // Track the "committed to local read set" ids in a ref so we can decide
    // outside a React state-updater callback (which may run lazily / more
    // than once) whether a given mark call actually introduces a new id
    // that needs to reach Firestore. `readArticleIdsRef` mirrors the
    // `readArticleIds` state and stays consistent by being updated at the
    // same time as the setState call below.
    const readArticleIdsRef = useRef(readArticleIds);
    useEffect(() => {
        readArticleIdsRef.current = readArticleIds;
    }, [readArticleIds]);

    const markArticleRead = useCallback((articleIdOrIds) => {
        if (!userUidRef.current) return;
        const rawList = Array.isArray(articleIdOrIds)
            ? articleIdOrIds
            : [articleIdOrIds];
        const seen = new Set();
        const newIds = [];
        rawList.forEach((raw) => {
            const id = normalizeArticleIdInput(raw);
            if (!id || seen.has(id)) return;
            seen.add(id);
            // Skip already-read (nothing to write) and already-pending
            // (already batched for the next flush) ids so re-marks are free.
            if (readArticleIdsRef.current.has(id)) return;
            if (pendingReadRef.current.has(id)) return;
            newIds.push(id);
        });
        if (newIds.length === 0) return;

        // Optimistic local update — the read-state dot flips immediately even
        // if the Firestore commit is still in flight.
        newIds.forEach((id) => pendingReadRef.current.add(id));
        const nextReadSet = new Set(readArticleIdsRef.current);
        newIds.forEach((id) => nextReadSet.add(id));
        readArticleIdsRef.current = nextReadSet;
        setReadArticleIds(nextReadSet);

        if (flushTimerRef.current !== null) return;
        flushTimerRef.current = window.setTimeout(() => {
            void flushPendingReads();
        }, MARK_READ_DEBOUNCE_MS);
    }, [flushPendingReads]);

    useEffect(() => () => {
        // On unmount, flush any pending reads synchronously (fire-and-forget
        // Firestore commit) so a fast tab-close doesn't drop the write.
        if (flushTimerRef.current !== null) {
            window.clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        if (pendingReadRef.current.size > 0) {
            const uid = userUidRef.current;
            if (uid) {
                const ids = Array.from(pendingReadRef.current);
                pendingReadRef.current = new Set();
                void markReadBatch(uid, ids);
            }
        }
    // The default writer is stable; pending reads are intentionally flushed
    // only at unmount rather than whenever an injected test writer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resolvedRecencyWindowHours = useMemo(() => {
        const raw = Number(recencyWindowHours);
        if (!Number.isFinite(raw) || raw <= 0) {
            return DEFAULT_NEWS_RECENCY_WINDOW_HOURS;
        }
        return raw;
    }, [recencyWindowHours]);

    return {
        newsByIdentity,
        moverThresholdPct,
        loading,
        error,
        lastUpdatedAt,
        refresh,
        readArticleIds,
        markArticleRead,
        recencyWindowHours: resolvedRecencyWindowHours,
    };
}
